import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkClass = scrolled
    ? 'text-foreground-700 hover:text-foreground-950'
    : 'text-white/90 hover:text-white';

  const logoClass = scrolled
    ? 'text-foreground-900'
    : 'text-white';

  const hamburgerClass = scrolled
    ? 'text-foreground-900'
    : 'text-white';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-gradient-to-b from-black/35 to-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className={`font-heading text-xl md:text-2xl font-bold tracking-tight whitespace-nowrap ${logoClass}`}>
              OOPBuy <span className="font-light">Spreadsheets</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {/* Home */}
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}
            >
              Home
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/oopbuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}
            >
              Shopping Guide
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${linkClass}`}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-lg border border-background-200 shadow-sm py-2 animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-96 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 bg-background-200">
                          <img
                            src={cat.imageUrl}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{cat.name}</p>
                          <p className="text-xs text-foreground-400 truncate">{cat.description}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Blog */}
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${linkClass}`}
            >
              Blog
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${hamburgerClass}`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-[fadeIn_0.15s_ease-out] max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-5 py-4 space-y-1">
            {/* Quick Nav Links */}
            <div className="flex gap-3 mb-4 pb-4 border-b border-background-200">
              <Link
                to="/"
                className="flex-1 text-center px-4 py-2.5 text-sm font-semibold bg-foreground-900 text-background-50 rounded-md hover:bg-foreground-800 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-home-4-line text-sm"></i>
                </span>
                Home
              </Link>
              <Link
                to="/oopbuy-spreadsheet-guide"
                className="flex-1 text-center px-4 py-2.5 text-sm font-semibold bg-secondary-100 text-secondary-800 rounded-md hover:bg-secondary-200 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                  <i className="ri-book-open-line text-sm"></i>
                </span>
                Guide
              </Link>
            </div>

            {/* Categories Section */}
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2 mt-1">Shop Categories</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 p-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-10 h-10 rounded-md overflow-hidden shrink-0 bg-background-200">
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                </a>
              ))}
            </div>

            {/* Bottom CTA */}
            <div className="mt-4 pt-4 border-t border-background-200">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-5 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now on kaspreadsheet.com
              </a>
              <Link
                to="/"
                className="block w-full text-center mt-2 px-5 py-2.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                View Blog Articles
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}